/**
 * SpeechRestorerEngine — the /speech-restorer chain, streamed.
 *
 * Streamed (2026-09-25): the chain used to run on the whole recording at once
 * (25 min of mono speech peaked at 3.8 GB) and the page refused anything
 * longer than ~21 min on a 4 GB laptop — a 50-minute lecture had to be cut in
 * three (feedback 2026-09-19). Here it runs block by block: memory is one
 * 30-s block plus the engines, whatever the length; the result goes to disk.
 *
 * Measured (2026-09-26): 165 files — 11 voices × 15 kinds of damage (noise of
 * four kinds at three levels, a 0.7 s room, a 1.3 s hall, a classroom
 * lecture, phone, muffled, cassette, hard clipping, clipping after MP3,
 * crackle) — DNSMOS P.835 and ESTOI, each file paired with its own input,
 * the depth rule fitted leaving one voice out. What the chain was before and
 * what it is now:
 *   OUT  the gate — the largest harm (without it OVRL +0.13, ESTOI +0.012;
 *        on the presets +0.22 / +0.04); the compressor (OVRL +0.06…0.09
 *        without); EQ (−0.05 OVRL on phone, muffled, cassette: it lifts the
 *        residual noise); the «bandwidth extension» (0.00 even when forced on
 *        narrow-band speech — it only ever ran where the old bandwidth reading
 *        was wrong); the one-pole de-reverb (+0.02); the sample-jump de-click
 *        (0.00); the median/MAD de-click of /audio-restoration (ESTOI −0.06
 *        on every file: consonants read as clicks).
 *   IN   de-clip (restoration-declip, flat runs only: hard clipping ESTOI
 *        +0.020, no file worse; a file without plateaus passes bit for bit);
 *        WPE, the late-reverb suppressor of /remove-reverb run on the whole
 *        speech band, where the room tail it measures says it matters (a
 *        1.3 s hall OVRL +0.17, ESTOI +0.026, a lecture +0.11/+0.04, dry
 *        speech 0); DeepFilterNet at a depth chosen from the measured SNR and
 *        tail (params); loudness.
 * New against the page's own settings, paired on the same files: Medium
 * OVRL +0.31, ESTOI +0.051, no file of 148 worse by 0.02 ESTOI; Maximum
 * +0.25 / +0.045. Against the untouched input the old Medium made 74 of 148
 * files less intelligible (by 0.02+), the new one 19 of 165. A noisy phone
 * call (11 more files, noise inside the channel) is capped at 24 dB — see
 * params.
 *
 * Streaming details:
 *   · de-clip runs per block with 0.1 s either side (Overlap) — plateaus are
 *     a few ms, the block edges never cut one;
 *   · WPE estimates its filters per block and the blocks are cross-faded
 *     (XfadeOverlap): −23 dB from the whole-file WPE, seams at −47 dB;
 *   · DeepFilterNet runs as ONE stream (DeepFilterProcessor.openStream —
 *     separate calls would flush the model at every seam);
 *   · loudness is a whole-file measurement by nature: pass A writes the
 *     restored signal to disk and measures it, pass B applies the gain and
 *     the limiter (rendered per block with 2 s of context, discarded).
 *
 * Input: every format FFmpeg decodes, read in blocks by the shared
 * AudioRangeSource (public/js/audio-range-source.js; the page loads it before
 * this file): a WORKERFS mount, never copied into the wasm heap, downmixed to
 * mono as the MEAN of the channels and resampled to 48 kHz inside FFmpeg.
 * Each block is decoded with 1 s of lead-in and 0.5 s of lead-out that are
 * trimmed off (atrim on the resampled stream), so seams are sample-continuous.
 *
 * Browser-only pieces (FFmpeg, DeepFilterNet, the WPE worker,
 * OfflineAudioContext, OutputSink, MP3Encoder) are reached through `deps`, so
 * the DSP units below load in Node for tests/js/speech-restorer-engine.test.mjs.
 */
(function (root) {
    'use strict';

    var SR = 48000;
    var BLOCK = 30 * SR;          // 1 440 000 — a multiple of 128 (render grid) and of 480 (DFN hop)
    var CONTEXT = 2 * SR;         // rendered and discarded before each Web Audio block

    function f32cat(a, b) {
        if (!a || !a.length) return b;
        if (!b || !b.length) return a;
        var o = new Float32Array(a.length + b.length);
        o.set(a, 0); o.set(b, a.length);
        return o;
    }
    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

    /* ── Overlap — run a block processor with discarded context ─────────────
       fn(Float32Array) → Promise<Float32Array> of the same length, aligned.
       `ctx` samples of already-emitted input go in front of each block and
       their output is dropped (the processor enters the block warmed up).
       `post` samples at the END of each block are held back and emitted from
       the next call, for processors whose last frame is computed against
       silence — the RNNoise door reads its output one frame ahead and flushes
       the last frame with zeros, so without a hold-back every seam carried
       one wrong frame (−31 dB, measured 2026-09-25). flush() emits the rest. */
    function Overlap(fn, ctx, post) {
        this.fn = fn; this.ctx = ctx; this.post = post || 0;
        this.hist = new Float32Array(0); this.pend = new Float32Array(0);
    }
    Overlap.prototype.push = async function (x, final) {
        this.pend = f32cat(this.pend, x);
        var emit = final ? this.pend.length : Math.max(0, this.pend.length - this.post);
        if (!emit) return new Float32Array(0);
        var lead = this.hist.length;
        var y = await this.fn(f32cat(this.hist, this.pend));
        var out = y.slice(lead, lead + emit);
        var done = f32cat(this.hist, this.pend.subarray(0, emit));
        this.hist = done.slice(Math.max(0, done.length - this.ctx));
        this.pend = this.pend.slice(emit);
        return out;
    };
    Overlap.prototype.flush = function () { return this.push(new Float32Array(0), true); };

    /* ── XfadeOverlap — for processors that ESTIMATE on the whole block ─────
       WPE fits its prediction filters to the block it is given, so two
       neighbouring blocks subtract slightly different tails: joined end to
       end they would step at the seam. Each call is run on [ctx | pending];
       the last `edge + xf` samples are held back (the end of a window is the
       least reliable part), and the next call's output for the first `xf` of
       them is cross-faded with what the previous call computed there. */
    function XfadeOverlap(fn, ctx, xf, edge) {
        this.fn = fn; this.ctx = ctx; this.xf = xf; this.edge = edge || 0;
        this.hist = new Float32Array(0); this.pend = new Float32Array(0); this.prev = null;
    }
    XfadeOverlap.prototype.push = async function (x, final) {
        this.pend = f32cat(this.pend, x);
        var hold = this.xf + this.edge;
        var emit = final ? this.pend.length : Math.max(0, this.pend.length - hold);
        if (!emit) return new Float32Array(0);
        var lead = this.hist.length;
        var y = await this.fn(f32cat(this.hist, this.pend));
        var out = y.slice(lead, lead + emit);
        if (this.prev) {
            var n = Math.min(this.prev.length, out.length);
            for (var i = 0; i < n; i++) { var w = (i + 0.5) / n; out[i] = this.prev[i] * (1 - w) + out[i] * w; }
        }
        this.prev = final ? null : y.slice(lead + emit, lead + emit + this.xf);
        var done = f32cat(this.hist, this.pend.subarray(0, emit));
        this.hist = done.slice(Math.max(0, done.length - this.ctx));
        this.pend = this.pend.slice(emit);
        return out;
    };
    XfadeOverlap.prototype.flush = function () { return this.push(new Float32Array(0), true); };

    /* ── WpeStage — late-reverb suppression in front of the denoiser ─────────
       `call(x, opts) → Promise<Float32Array>` is the page's worker (WPE is
       minutes of arithmetic on an hour of lecture). Blocks are estimated one
       by one and joined by cross-fades (XfadeOverlap: 2 s context, 0.5 s
       fade, 0.25 s of each block's end never used). WPE is an improvement,
       not a requirement: a worker that cannot start, dies, or answers with the
       wrong length hands the block back untouched from then on, and `state`
       says so instead of the recording failing. */
    function WpeStage(call, opts) {
        var st = this.state = { on: true, failed: null, blocks: 0 };
        this.ov = new XfadeOverlap(function (x) {
            if (st.failed) return Promise.resolve(x);
            return Promise.resolve().then(function () { return call(x, opts); }).then(function (y) {
                if (!y || y.length !== x.length) throw new Error('wpe: length ' + (y && y.length) + ' for ' + x.length);
                st.blocks++;
                return y;
            }).catch(function (e) { st.failed = String((e && e.message) || e).slice(0, 120); return x; });
        }, 2 * SR, SR / 2, SR / 4);
    }
    WpeStage.prototype.push = function (x, final) { return this.ov.push(x, final); };

    /* ── FFT (radix-2, in place) ─────────────────────────────────────────── */
    function fft(re, im) {
        var n = re.length, i, j = 0, len, k;
        for (i = 1; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (len = 2; len <= n; len <<= 1) {
            var ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang), half = len >> 1;
            for (i = 0; i < n; i += len) {
                var cr = 1, ci = 0;
                for (k = 0; k < half; k++) {
                    var a = i + k, b = a + half;
                    var tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
                    re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
                    var ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
    }
    var _winCache = {};
    function windowOf(n, kind) {
        var key = kind + n;
        if (_winCache[key]) return _winCache[key];
        var w = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var x = 2 * Math.PI * i / n;
            w[i] = kind === 'blackman' ? 0.42 - 0.5 * Math.cos(x) + 0.08 * Math.cos(2 * x) : 0.5 - 0.5 * Math.cos(x);
        }
        return (_winCache[key] = w);
    }
    /** dB magnitude spectrum of x (length = power of two), bins 0..n/2-1. */
    function spectrumDb(x, kind) {
        var n = x.length, w = windowOf(n, kind || 'hann');
        var re = new Float32Array(n), im = new Float32Array(n);
        for (var i = 0; i < n; i++) re[i] = x[i] * w[i];
        fft(re, im);
        var out = new Float32Array(n / 2);
        for (i = 0; i < n / 2; i++) out[i] = 20 * Math.log10(Math.sqrt(re[i] * re[i] + im[i] * im[i]) / n * 2 + 1e-12);
        return out;
    }

    /* ── Frame statistics — honest before/after measures ──────────────────
       20-ms frame RMS values, kept (4 bytes per 20 ms: 2 MB for three hours),
       so percentiles are exact over the whole recording. */
    function FrameStats(sr) {
        this.fs = Math.floor(sr * 0.02);
        this.vals = new Float32Array(4096); this.n = 0;
        this.pend = new Float32Array(0);
        this.sumSq = 0; this.count = 0; this.clip = 0; this.peak = 0;
    }
    FrameStats.prototype.push = function (x) {
        for (var i = 0; i < x.length; i++) {
            var v = x[i];
            this.sumSq += v * v;
            if (v > 0.99 || v < -0.99) this.clip++;
            if (v > this.peak) this.peak = v; else if (-v > this.peak) this.peak = -v;
        }
        this.count += x.length;
        var buf = f32cat(this.pend, x), fs = this.fs, n = Math.floor(buf.length / fs) * fs;
        for (var o = 0; o < n; o += fs) {
            var s = 0;
            for (var j = 0; j < fs; j++) s += buf[o + j] * buf[o + j];
            if (this.n === this.vals.length) { var g = new Float32Array(this.vals.length * 2); g.set(this.vals); this.vals = g; }
            this.vals[this.n++] = Math.sqrt(s / fs);
        }
        this.pend = buf.slice(n);
    };
    /** Resets the partial frame — windows of the analysis are not contiguous. */
    FrameStats.prototype.cut = function () { this.pend = new Float32Array(0); };
    /* Frames quieter than −100 dBFS are digital silence (the recorder's gaps,
       an edit's zeros), not the recording's noise. Until 2026-09-25 they
       entered the percentile, a noisy lecture with a few seconds of zeros read
       «noise −200 dB», and the diagnosis called it clean. */
    var SILENT = 1e-5;
    FrameStats.prototype.summary = function () {
        var n = this.n;
        if (!n) return null;
        var all = this.vals.subarray(0, n), pool = [];
        for (var p = 0; p < n; p++) if (all[p] >= SILENT) pool.push(all[p]);
        if (pool.length < Math.max(10, n * 0.05)) pool = Array.prototype.slice.call(all);
        n = pool.length;
        var s = pool.sort(function (a, b) { return a - b; });
        var q10 = Math.floor(n * 0.1), q90 = Math.floor(n * 0.9), i;
        var lo = 0; for (i = 0; i < q10; i++) lo += s[i];
        lo = q10 > 0 ? lo / q10 : 0.0001;
        var hi = 0; for (i = q90; i < n; i++) hi += s[i];
        hi = (n - q90) > 0 ? hi / (n - q90) : 0.001;
        var noiseDb = 20 * Math.log10(Math.max(lo, 1e-10));
        var speechDb = 20 * Math.log10(Math.max(hi, 1e-10));
        // For BEFORE/AFTER the pauses count, silence included, but no frame
        // reads quieter than −80 dBFS: a restored pause is near-silent, and
        // dropping it (as the diagnosis must) would make the «after» noise the
        // quietest syllables — «23 → 12 dB» on a file whose pauses got cleaner.
        var FLOOR = Math.pow(10, -80 / 20), allS = Array.prototype.slice.call(all).sort(function (a, b) { return a - b; });
        var q = Math.max(1, Math.floor(allS.length * 0.1)), fl = 0;
        for (var fi = 0; fi < q; fi++) fl += Math.max(FLOOR, allS[fi]);
        var floorDb = 20 * Math.log10(fl / q);
        return {
            noiseDb: noiseDb,
            floorDb: floorDb,
            speechDb: speechDb,
            snrDb: speechDb - noiseDb,
            rmsDb: 10 * Math.log10(this.sumSq / Math.max(1, this.count) + 1e-12),
            clipPct: this.count ? this.clip / this.count * 100 : 0,
            peak: this.peak
        };
    };

    /* ── Spectrogram / spectrum taps ─────────────────────────────────────
       A tap watches one stream (absolute sample positions) and computes an
       FFT at pre-chosen positions as the stream passes them: nCols columns
       spread over the recording for the spectrogram, one 4096 window at the
       middle for the spectrum. The columns are stored as bytes (dB mapped to
       0..255), so a three-hour file costs a few hundred KB, and the page
       draws them with putImageData — the old per-pixel DFT froze the tab for
       5.2 s on average and up to 28 s per click (94 inp_slow, RU 90 d). */
    var COL_FFT = 1024, SPEC_FFT = 4096, DB_FLOOR = -100, DB_CEIL = -10;
    /* The spectrum (and the bandwidth read from it) is one 4096 window — the
       page's own method — but not blindly the middle one: a middle window that
       falls into a pause measured the noise, or nothing at all («24 kHz» on
       zeros). The before-tap keeps the LOUDEST window among the column
       positions; the after-tap is pointed at the same moment (specAt). */
    function Tap(totalSamples, nCols, specAt) {
        this.total = Math.max(1, totalSamples);
        this.nCols = nCols;
        this.cols = new Array(nCols);
        this.pos = 0;
        this.hist = new Float32Array(0);
        this.nextCol = 0;
        this.specAt = (typeof specAt === 'number' && specAt >= 0) ? specAt : -1;
        this.bestE = -1; this.bestPos = -1;
        this.spectrum = null;
    }
    Tap.prototype._colStart = function (c) {
        return Math.max(0, Math.floor((c + 0.5) * this.total / this.nCols) - COL_FFT / 2);
    };
    Tap.prototype.push = function (x) {
        var start = this.pos - this.hist.length;      // absolute index of buf[0]
        var buf = f32cat(this.hist, x);
        this._columns(buf, start, false);
        if (this.specAt >= 0 && !this.spectrum && this.specAt >= start && this.specAt + SPEC_FFT <= start + buf.length) {
            this.spectrum = spectrumDb(buf.subarray(this.specAt - start, this.specAt + SPEC_FFT - start), 'blackman');
        }
        this.pos += x.length;
        var keep = Math.max(COL_FFT, SPEC_FFT);
        this.hist = buf.length > keep ? buf.slice(buf.length - keep) : buf.slice();
    };
    /* A column waits until its whole window is in — the spectrum candidate's
       4096 too — so the result does not depend on where the stream was cut.
       At the end (last) the remaining columns take what there is. */
    Tap.prototype._columns = function (buf, start, last) {
        var end = start + buf.length;
        var need = this.specAt < 0 ? SPEC_FFT : COL_FFT;
        while (this.nextCol < this.nCols) {
            var s = this._colStart(this.nextCol);
            if (s + (last ? COL_FFT : need) > end) break;
            if (s >= start) {
                var d = spectrumDb(buf.subarray(s - start, s - start + COL_FFT), 'hann');
                var col = new Uint8Array(d.length);
                for (var i = 0; i < d.length; i++) col[i] = Math.round(clamp((d[i] - DB_FLOOR) / (DB_CEIL - DB_FLOOR), 0, 1) * 255);
                this.cols[this.nextCol] = col;
                if (this.specAt < 0 && s + SPEC_FFT <= end) {
                    var e = 0, w = buf.subarray(s - start, s - start + SPEC_FFT);
                    for (var k = 0; k < w.length; k++) e += w[k] * w[k];
                    if (e > this.bestE) { this.bestE = e; this.bestPos = s; this.spectrum = spectrumDb(w, 'blackman'); }
                }
            }
            this.nextCol++;
        }
    };
    /** The recording turned out shorter than announced: fill the missing
     *  columns from the nearest measured one so the picture has no holes. */
    Tap.prototype.finish = function () {
        this._columns(this.hist, this.pos - this.hist.length, true);
        var last = null;
        for (var c = 0; c < this.nCols; c++) { if (this.cols[c]) last = this.cols[c]; else if (last) this.cols[c] = last; }
        if (!this.spectrum && this.hist.length >= 256) {
            var n = 1 << Math.floor(Math.log2(Math.min(SPEC_FFT, this.hist.length)));
            this.spectrum = spectrumDb(this.hist.subarray(this.hist.length - n), 'blackman');
        }
    };

    /* ── The depth — measured, 2026-09-26 (see the header) ───────────────────
       One question decides how hard DeepFilterNet may press: is the speech
       buried (in noise, or in a room's tail) or already clean? Buried speech
       gains from depth — 40 dB: OVRL +0.13…0.50, ESTOI +0.02…0.04; clean
       speech LOSES intelligibility to it — phone, muffled, cassette, a 0.7 s
       room: ESTOI −0.03…−0.06 at 30–40 dB, half the files worse. The answer
       is two measured numbers: the speech above the pauses (dynamicRange,
       dB) below 20, or the room tail (measureTail, dB) at 16 or less. Fitted
       leaving one voice out, the rule held on the unseen voice in 8 of 10
       folds: OVRL +0.13, ESTOI +0.016, one file in 120 worse by 0.02.
       Deeper than 40 dB removes nothing more (BAK +0.57 against +0.63) and
       distorts more (SIG −0.28 against −0.16).
       A NARROW CHANNEL caps the depth at 24 dB (bandLimit < 6 kHz — a phone
       call). Measured on a noisy call (noise inside the channel, SNR 9–17 dB,
       11 voices): 40 dB costs ESTOI −0.041 against the old page, 8 files of 11
       worse; 24 dB keeps intelligibility (−0.007) and gains OVRL +0.21; the
       gentle depth keeps it too with OVRL +0.02. Clean narrow-band speech
       stays gentle: the rule never calls it buried.
       The presets are what the person knows that the measurement may not:
       noisy → buried; phone, old, muffled, radio → narrow-band speech, never
       deeper than 24; conference → WPE whatever the tail. */
    var DEEP_SNR_DB = 20, DEEP_TAIL_DB = 16, DEEP_ATTEN_DB = 40, NARROW_HZ = 6000, NARROW_ATTEN_DB = 24;
    var PRESET_NARROW = { phone: 1, old: 1, muffled: 1, radio: 1 };
    var PRESET_NAMES = ['auto', 'phone', 'old', 'noisy', 'muffled', 'conference', 'radio'];
    function depthClass(preset, diag) {
        diag = diag || {};
        var snr = typeof diag.dynamicRange === 'number' ? diag.dynamicRange : 30;
        var tail = typeof diag.tailDb === 'number' ? diag.tailDb : null;
        var buried = preset === 'noisy' || snr < DEEP_SNR_DB || (tail !== null && tail <= DEEP_TAIL_DB);
        if (!buried) return 'gentle';
        var narrow = !!PRESET_NARROW[preset] || (typeof diag.bandLimit === 'number' && diag.bandLimit < NARROW_HZ);
        return narrow ? 'deep-narrow' : 'deep';
    }
    /* The gentle depth: by the noise floor 12/18/26 dB (the page's depth
       before 2026-09-26). On speech that is not buried the SHARE of the
       network's output trades quality for intelligibility, monotonically —
       measured against the untouched input on 86 such files (cassette,
       clicks, clipping, clipping + MP3, muffled, a clean call, a 0.7 s room,
       kitchen clatter), OVRL / ESTOI / files worse by 0.02:
         share 0 (de-clip only) −0.02 / 0.000 / 2   · 0.5  +0.05 / +0.001 / 5
         0.7                    +0.09 / −0.005 / 19  · 0.85–0.9 +0.10 / −0.015 / 37
         1.0                    +0.09 / −0.029 / 50  · 24 dB at 1.0 +0.07 / −0.054 / 64
       The network reads clipping and clicks as noise: at 0.85–0.9 a clipped
       MP3 lost 0.049 ESTOI, at 0.7 — 0.025, at 0.5 — 0.009. The second number
       is the share the page used as Medium before; it is Strong now. */
    function gentleDepth(diag) {
        var nf = (diag && typeof diag.noiseFloor === 'number') ? diag.noiseFloor : -50;
        return [nf < -45 ? 12 : (nf < -32 ? 18 : 26), nf > -25 ? 0.95 : (nf > -40 ? 0.9 : 0.85)];
    }
    /* Intensity: Light · Medium · Strong · Maximum. Medium is the measured
       choice. On speech that is not buried: Light 0.5 of the network (no
       intelligibility lost), Medium 0.7, Strong 0.85–0.9 (the quality peak),
       Maximum 24 dB at full share — it is what helps against impulsive noise
       between quiet pauses (kitchen clatter: OVRL +0.31 against +0.25 at
       Strong), and costs a clean recording intelligibility, which the page
       says next to the choice. Buried speech has nothing deeper that helps:
       Strong and Maximum stay at 40 (24 in a narrow channel). */
    function params(preset, diag, intensity) {
        diag = diag || {};
        var i = (intensity === 0 || intensity === 1 || intensity === 2 || intensity === 3) ? intensity : 1;
        var cls = depthClass(preset, diag), g = gentleDepth(diag);
        var ladder = cls === 'deep'
            ? [[24, 1], [DEEP_ATTEN_DB, 1], [DEEP_ATTEN_DB, 1], [DEEP_ATTEN_DB, 1]]
            : cls === 'deep-narrow'
            ? [[g[0], g[1]], [NARROW_ATTEN_DB, 1], [NARROW_ATTEN_DB, 1], [NARROW_ATTEN_DB, 1]]
            : [[g[0], 0.5], [g[0], 0.7], [g[0], g[1]], [24, 1]];
        return {
            cls: cls,
            attenDb: ladder[i][0],
            dfnWet: ladder[i][1],
            // WPE: null = decided by the measured tail (run); conference forces it
            wpe: preset === 'conference' ? true : null,
            // RNNoise, when DeepFilterNet cannot load
            nrPasses: cls === 'gentle' ? 1 : 2,
            nrStrength: [0.45, 0.75, 0.85, 0.9][i]
        };
    }
    /** The pre-level gain: quiet input goes toward −20 dB RMS (cap +30 dB). */
    function preGain(rmsDb) {
        var r = +(+rmsDb).toFixed(1);
        if (!(r < -28) || !isFinite(r)) return 1;
        return Math.min(Math.pow(10, (-20 - r) / 20), 32);
    }

    /* ── The final stage (pass B): loudness gain + limiter, rendered per block
       with 2 s of context in front (discarded), block starts on the 128-frame
       render grid. The limiter (DynamicsCompressor) holds a 6 ms look-ahead:
       the block is rendered `lead` frames longer and read `lead` frames later,
       so the file keeps the input's timing (it came out 288 frames late and lost
       its end, 2026-10-09). Those last `lead` frames of a block are computed
       against silence — the caller holds them back (Overlap `post`). */
    function makeFinalRender(OAC, gainLinear, lead) {
        lead = lead || 0;
        return function (x) {
            var ctx = new OAC(1, x.length + lead, SR);
            var buf = ctx.createBuffer(1, x.length, SR);
            buf.copyToChannel(x, 0);
            var src = ctx.createBufferSource(); src.buffer = buf;
            var g = ctx.createGain(); g.gain.value = gainLinear;
            var lim = ctx.createDynamicsCompressor();
            lim.threshold.value = -1; lim.ratio.value = 20; lim.attack.value = 0.001; lim.release.value = 0.01; lim.knee.value = 0;
            src.connect(g); g.connect(lim); lim.connect(ctx.destination);
            src.start();
            return ctx.startRendering().then(function (b) { return new Float32Array(b.getChannelData(0).subarray(lead, lead + x.length)); });
        };
    }
    /* The final stage through the shared true-peak limiter (MasteringLimiter,
       streamed): gain, then a ceiling of −1 dBTP the output cannot pass, aligned
       with the input (no look-ahead shift) and seamless across blocks. The
       DynamicsCompressor stage above is not a brick wall: on real speech after
       the loudness gain it let peaks through to 1.15–1.34 (197–608 samples over
       full scale per clip, clipped hard by the 16-bit WAV) — measured 2026-10-09.
       Same push(x, final) shape as Overlap. Null when the limiter is absent. */
    function makeFinalStream(gainLinear) {
        var ML = typeof window !== 'undefined' && window.MasteringLimiter;
        if (!ML || !ML.createStreamLimiter) return null;
        var lim = ML.createStreamLimiter(SR, 1, { ceilingDb: -1, preGainDb: 20 * Math.log10(Math.max(gainLinear, 1e-6)) });
        function cat(blocks) {
            var n = 0, i, k = 0;
            for (i = 0; i < blocks.length; i++) n += blocks[i][0].length;
            var o = new Float32Array(n);
            for (i = 0; i < blocks.length; i++) { o.set(blocks[i][0], k); k += blocks[i][0].length; }
            return o;
        }
        return {
            push: function (x, final) {
                var out = cat(lim.push([x]));
                if (final) out = f32cat(out, cat(lim.flush().blocks));
                return Promise.resolve(out);
            }
        };
    }
    /** The page's "LUFS" step: RMS of the whole compressed signal, silence
     *  included, toward −16; the gain is clamped to [0.01, 10]. */
    function loudnessGain(sumSq, count) {
        var cur = -0.691 + 10 * Math.log10(Math.max(sumSq / Math.max(1, count), 1e-20));
        return clamp(Math.pow(10, (-16 - cur) / 20), 0.01, 10);
    }

    /* ── Sources ───────────────────────────────────────────────────────────
       read(startSample, n) → Float32Array of ≤ n mono 48 kHz samples; fewer
       means the recording ended. The file is read by the shared
       AudioRangeSource (public/js/audio-range-source.js) — the 30-s blocks,
       the whole-second lead-in, the trim by samples and the mono mean were
       born here and moved there on 2026-10-14, so every tool reading «any
       place of a long recording» reads it the same way. */
    async function openFfmpegSource(file, deps) {
        var ARS = (deps && deps.AudioRangeSource) || root.AudioRangeSource;
        if (!ARS) { var miss = new Error('AudioRangeSource is not loaded'); miss.code = 'SR_NO_SOURCE'; throw miss; }
        var src = await ARS.open(file, { FFmpegProcessor: deps.FFmpegProcessor });
        async function read(startSample, n) {
            try {
                return (await src.read(startSample, n, SR, 1))[0];
            } catch (e) {
                if (e && e.code === 'ARS_DECODE') e.code = 'SR_DECODE';
                throw e;
            }
        }
        return {
            kind: 'ffmpeg', info: src.info, read: read,
            close: function () { return src.close(); }
        };
    }
    /** A decoded AudioBuffer (the whole-file fallback when FFmpeg cannot load):
     *  downmix + resample to 48 kHz once, then serve slices. */
    async function openBufferSource(audioBuffer, deps) {
        var OAC = deps.OfflineAudioContext;
        var len48 = Math.ceil(audioBuffer.duration * SR);
        var ctx = new OAC(1, len48, SR);
        var src = ctx.createBufferSource(); src.buffer = audioBuffer;
        // Web Audio downmixes stereo → mono as 0.5·(L + R) (speakers
        // interpretation), the same sum the FFmpeg source uses.
        src.connect(ctx.destination); src.start();
        var mono = (await ctx.startRendering()).getChannelData(0);
        return {
            kind: 'buffer',
            info: { duration: audioBuffer.duration, sampleRate: audioBuffer.sampleRate, channels: audioBuffer.numberOfChannels },
            read: function (s, n) { return Promise.resolve(mono.slice(Math.min(s, mono.length), Math.min(s + n, mono.length))); },
            close: function () { mono = null; }
        };
    }

    /* ── Analysis — the diagnosis the presets read ────────────────────────
       Files up to 90 s are read whole (the numbers are then the page's own).
       Longer ones: 12 windows of 5 s spread evenly (starts on whole seconds),
       enough for percentiles of 3 000 frames and far cheaper than decoding an
       hour to draw five tiles. */
    /* ── Speech bandwidth — how high the speech stands above the pauses ───────
       The long-term spectrum of the loudest fifth of 2048-sample frames
       (speech) against the quietest fifth (the pauses), third-octave
       smoothed; the bandwidth is the highest frequency where speech is 6 dB
       above the pauses and within 70 dB of its own peak. Until 2026-09-26 the
       page read ONE 4096 window at the middle and took the highest bin within
       40 dB of the peak: any noise filled the top, and a telephone call read
       18 kHz on average (a hiss-free one 3.4 kHz), a muffled one 17 kHz.
       Measured on the corpus: phone 3.4–3.9 kHz, muffled 2.3–3.4, cassette
       (7 kHz tape) 7.0–7.5, clean studio speech 17–24. In strong noise the
       number drops too — that is true of the speech (its top is under the
       noise), and the page says so in those words.
       The CHANNEL width is a different question — the upper edge of the
       long-term spectrum of ALL frames, 60 dB below its top: a noisy phone
       call (noise inside the 300–3400 Hz channel) reads 3.6–3.9 kHz, every
       other recording of the corpus 15.7–24 kHz, noisy wide-band speech
       included (its top is buried, not missing). params() reads it. */
    function SpeechBand() { this.spec = []; this.energy = []; }
    var SB_N = 2048;
    SpeechBand.prototype.push = function (x) {
        var w = windowOf(SB_N, 'hann'), re = new Float64Array(SB_N), im = new Float64Array(SB_N);
        for (var f = 0; f + SB_N <= x.length; f += SB_N) {
            for (var i = 0; i < SB_N; i++) { re[i] = x[f + i] * w[i]; im[i] = 0; }
            fft(re, im);
            var p = new Float32Array(SB_N / 2 + 1), e = 0;
            for (var k = 0; k <= SB_N / 2; k++) { p[k] = re[k] * re[k] + im[k] * im[k]; e += p[k]; }
            if (!(e > 1e-12)) continue;                 // digital silence says nothing
            this.spec.push(p); this.energy.push(e);
        }
    };
    SpeechBand.prototype.result = function (sr) {
        var n = this.spec.length;
        if (n < 10) return null;
        var order = this.energy.map(function (e, i) { return i; }).sort(function (a, b) { return this.energy[a] - this.energy[b]; }.bind(this));
        var q = Math.max(2, Math.floor(n * 0.2)), K = SB_N / 2 + 1;
        var loud = new Float64Array(K), quiet = new Float64Array(K), all = new Float64Array(K), i, k;
        for (i = 0; i < q; i++) {
            var a = this.spec[order[n - 1 - i]], b = this.spec[order[i]];
            for (k = 0; k < K; k++) { loud[k] += a[k]; quiet[k] += b[k]; }
        }
        for (i = 0; i < n; i++) { var c = this.spec[i]; for (k = 0; k < K; k++) all[k] += c[k]; }
        var L = new Float64Array(K), Q = new Float64Array(K), A = new Float64Array(K);
        for (k = 0; k < K; k++) {
            L[k] = 10 * Math.log10(loud[k] / q + 1e-20); Q[k] = 10 * Math.log10(quiet[k] / q + 1e-20);
            A[k] = 10 * Math.log10(all[k] / n + 1e-20);
        }
        var hz = sr / SB_N, Ls = smoothThird(L), Qs = smoothThird(Q), As = smoothThird(A), top = -Infinity, topA = -Infinity;
        for (k = 1; k < K; k++) { if (Ls[k] > top) top = Ls[k]; if (As[k] > topA) topA = As[k]; }
        var edge = 0, limit = 0;
        for (k = Math.ceil(200 / hz); k < K; k++) {
            if (Ls[k] - Qs[k] >= 6 && Ls[k] >= top - 70) edge = k;
            if (As[k] >= topA - 60) limit = k;
        }
        return { speech: Math.round(edge * hz), limit: Math.round(limit * hz) };
    };
    /* third-octave smoothing of a dB spectrum (mean of dB), prefix sums */
    function smoothThird(D) {
        var K = D.length, c = new Float64Array(K + 1), r = Math.pow(2, 1 / 6), out = new Float64Array(K), k;
        for (k = 0; k < K; k++) c[k + 1] = c[k] + D[k];
        for (k = 1; k < K; k++) {
            var lo = Math.max(1, Math.floor(k / r)), hi = Math.min(K - 1, Math.ceil(k * r));
            out[k] = (c[hi + 1] - c[lo]) / (hi - lo + 1);
        }
        return out;
    }

    async function analyze(source, opts) {
        opts = opts || {};
        var dur = source.info.duration || 0;
        var windows = [];
        if (dur && dur <= 90) windows.push([0, Math.ceil((dur + 2) * SR)]);
        else if (!dur) windows.push([0, 90 * SR]);          // length unknown: judge the first 90 s
        else {
            var K = 12, W = 5;
            for (var k = 0; k < K; k++) windows.push([Math.floor(k * (dur - W - 1) / (K - 1)) * SR, W * SR]);
        }
        var st = new FrameStats(SR), band = new SpeechBand(), total = 0;
        for (var i = 0; i < windows.length; i++) {
            if (opts.signal && opts.signal.aborted) throw abortErr();
            var x = await source.read(windows[i][0], windows[i][1]);
            st.cut(); st.push(x); band.push(x); total += x.length;
            if (opts.onProgress) opts.onProgress((i + 1) / windows.length);
        }
        var sum = st.summary();
        if (!sum) return null;
        if (!dur) dur = total / SR;
        var bw = band.result(SR);
        return {
            noiseFloor: Math.round(sum.noiseDb),
            // the speech above its own pauses, dB — the SNR the depth reads
            dynamicRange: Math.round(sum.snrDb),
            bandwidth: bw == null ? SR / 2 : bw.speech,
            // how wide the channel itself is (a phone call: ~4 kHz) — params() reads it
            bandLimit: bw == null ? SR / 2 : bw.limit,
            clippingPct: Math.round(sum.clipPct * 100) / 100,
            rmsDb: sum.rmsDb,
            peak: sum.peak,
            duration: dur,
            sampled: windows.length > 1 || !source.info.duration,
            // measureTail reads the same windows again, through WPE
            windows: windows
        };
    }

    function abortErr() { var e = new Error('cancelled'); e.name = 'AbortError'; return e; }

    /* ── The run ───────────────────────────────────────────────────────────
       opts: { preset, intensity, diag, signal, onProgress(frac, phase),
               outputs: { mp3Kbps? }, deps } → result (see bottom). */
    async function run(source, opts) {
        var deps = opts.deps;
        var OAC = deps.OfflineAudioContext;
        var diag = opts.diag || {};
        var p = params(opts.preset, diag, opts.intensity);
        // WPE where the measured tail says it matters (measureTail); a tail
        // that was never measured takes it on — harmless on dry speech, only
        // slower.
        if (p.wpe == null) p.wpe = !(typeof diag.tailDb === 'number') || diag.tailDb <= TAIL_ON_DB;
        // a measurement may vary any of it (tests/scratch benches); production passes no tweak
        if (typeof opts.tweak === 'function') opts.tweak(p, diag);
        var sig = opts.signal;
        var check = function () { if (sig && sig.aborted) throw abortErr(); };
        var dur = source.info.duration || diag.duration || 0;
        var totalEst = Math.max(1, Math.round(dur * SR));
        var t = { decode: 0, dfn: 0, dsp: 0, wpe: 0, write: 0, final: 0, mp3: 0 };
        var now = function () { return (typeof performance !== 'undefined' ? performance.now() : Date.now()); };

        var g0 = p.noPreGain ? 1 : preGain(diag.rmsDb);
        // De-clip first, on the raw input: the plateaus a too-hot recording
        // left are rebuilt before anything else sees them (restoration-declip:
        // only FLAT runs are touched, so a file without them passes bit for
        // bit). Its ceiling gain is not used here — per block it would step
        // the level; loudness is normalised in pass B.
        var clip = { runs: 0, samples: 0 };
        var declip = (deps.declip && diag.peak >= 0.5 && p.declip !== false) ? new Overlap(function (x) {
            var o = x.slice(), r = deps.declip.repair(x, o, diag.peak, {});
            clip.runs += r.runs; clip.samples += r.samples;
            return Promise.resolve(o);
        }, 4800, 4800) : null;
        var wpe = (p.wpe && deps.wpe) ? new WpeStage(deps.wpe, p.wpe === true ? {} : p.wpe) : null;
        var wpeState = wpe ? wpe.state : { on: false, failed: null, blocks: 0 };
        var tapBefore = new Tap(totalEst, opts.cols || 720);
        var tapAfter = null;   // made after pass A: it measures where the before-tap found speech
        var statBefore = new FrameStats(SR), statAfter = new FrameStats(SR);

        // Denoiser: one DeepFilterNet stream; RNNoise (per block, with context)
        // when DeepFilterNet cannot load.
        var engine = 'none', dfs = null, rnn = [];
        if (p.nrPasses > 0) {
            if (deps.dfn) {
                deps.dfn.setAttenLimit(p.attenDb);
                dfs = deps.dfn.openStream();
                engine = 'dfn3';
            } else if (deps.rnnoise && typeof deps.rnnoise.openStream === 'function') {
                // One RNNoise stream per pass: the network is recurrent, so
                // blocks processed apart never meet at a seam (see openStream).
                engine = 'rnnoise';
                for (var pass = 0; pass < p.nrPasses; pass++) rnn.push(deps.rnnoise.openStream(p.nrStrength * (pass === 0 ? 1.0 : 0.5)));
            }
        }
        var dfnStrength = p.dfnWet;

        // Pass-A sink: the restored signal before loudness, float32, raw.
        var tmp = await deps.createSink({ mime: 'application/octet-stream', name: 'sr-pass-a' });
        var outSink = null, mp3 = null, mp3Sink = null, beforeSink = null, beforeWav = null;
        // The page plays "before" from the original file. A format the browser
        // cannot play itself (wma, amr…) gets the decoded input written here.
        if (opts.keepBefore) {
            beforeSink = await deps.createSink({ mime: 'audio/wav', name: 'sr-before' });
            beforeWav = deps.wavWriter(beforeSink, SR, 1, 16);
        }
        var sumSq = 0, count = 0, inSamples = 0;
        var passAShare = 0.9;

        async function written(c) {
            if (!c.length) return;
            for (var i = 0; i < c.length; i++) sumSq += c[i] * c[i];
            count += c.length;
            var t3 = now();
            await tmp.write(new Uint8Array(c.buffer, c.byteOffset, c.byteLength));
            t.write += now() - t3;
        }

        try {
            // ── pass A: de-clip → pre-level → WPE → DeepFilterNet → disk ──
            for (var pos = 0; ; pos += BLOCK) {
                check();
                var td = now();
                var x = await source.read(pos, BLOCK);
                t.decode += now() - td;
                var last = x.length < BLOCK;
                if (x.length) {
                    tapBefore.push(x); statBefore.push(x); inSamples += x.length;
                    if (beforeWav) await beforeWav.writeChunk([x]);
                }
                var xc = x;
                if (declip) { var tc = now(); xc = await declip.push(x, last); t.dsp += now() - tc; }
                // Pre-level on a copy: the taps above may still hold x.
                var xg = new Float32Array(xc.length);
                for (var i = 0; i < xc.length; i++) xg[i] = xc[i] * g0;
                if (wpe) { var tw0 = now(); xg = await wpe.push(xg, last); t.wpe += now() - tw0; }
                if (xg.length) {
                    check();
                    var tn = now();
                    var y = xg;
                    if (dfs) y = (await dfs.process(deps.makeBuffer(xg), dfnStrength)).channels[0];
                    else for (var r = 0; r < rnn.length; r++) { y = rnn[r].process(y); if (r + 1 < rnn.length) await new Promise(function (res) { setTimeout(res, 0); }); }
                    t.dfn += now() - tn;
                    await written(y);
                }
                if (opts.onProgress) opts.onProgress(passAShare * Math.min(1, inSamples / totalEst), 'restore', inSamples / SR);
                if (last) break;
            }
            check();
            var tf = now();
            if (dfs) { var yEnd = (await dfs.finish(dfnStrength)).channels[0]; dfs = null; t.dfn += now() - tf; await written(yEnd); }
            else if (rnn.length) {
                // Each pass hands its held-back tail to the next, then flushes.
                var yr = new Float32Array(0);
                for (var rf = 0; rf < rnn.length; rf++) yr = f32cat(rnn[rf].process(yr), rnn[rf].finish());
                t.dfn += now() - tf;
                await written(yr);
            }
            var passA = await tmp.finish();

            // ── pass B: loudness gain + limiter → the delivered WAV (+ MP3) ──
            var gain = loudnessGain(sumSq, count);
            tapAfter = new Tap(totalEst, opts.cols || 720, tapBefore.bestPos);
            var fin = makeFinalStream(gain);
            if (!fin) {
                var NL = typeof window !== 'undefined' && window.NodeLatency;
                var limLead = NL ? await NL.compressor(SR) : 0;
                fin = new Overlap(makeFinalRender(OAC, gain, limLead), CONTEXT, limLead);
            }
            outSink = await deps.createSink({ mime: 'audio/wav', name: 'sr-restored' });
            var wav = deps.wavWriter(outSink, SR, 1, 16);
            if (opts.outputs && opts.outputs.mp3Kbps && deps.mp3Stream) {
                mp3Sink = await deps.createSink({ mime: 'audio/mpeg', name: 'sr-restored-mp3' });
                mp3 = await deps.mp3Stream({ sampleRate: SR, channels: 1, bitrate: opts.outputs.mp3Kbps, accumulate: false });
            }
            var total = count, done = 0;
            for (var off = 0; off < total; off += BLOCK) {
                check();
                var n = Math.min(BLOCK, total - off);
                var tr = now();
                var raw = await passA.slice(off * 4, (off + n) * 4).arrayBuffer();
                // The last block flushes: both final stages hold samples back
                // (the limiter's look-ahead / the stream's halo).
                var o = await fin.push(new Float32Array(raw), off + n >= total);
                t.final += now() - tr;
                if (!o.length) { done += n; continue; }
                tapAfter.push(o); statAfter.push(o);
                var tw = now();
                await wav.writeChunk([o]);
                t.write += now() - tw;
                if (mp3) {
                    var tm = now();
                    var bytes = await mp3.feed([o.slice()]);
                    if (bytes && bytes.byteLength) await mp3Sink.write(new Uint8Array(bytes));
                    t.mp3 += now() - tm;
                }
                done += n;
                if (opts.onProgress) opts.onProgress(passAShare + (1 - passAShare) * (done / Math.max(1, total)), 'finish', inSamples / SR);
            }
            var wavBlob = await wav.finalize();
            var beforeBlob = beforeWav ? await beforeWav.finalize() : null;
            var mp3Blob = null;
            if (mp3) {
                var fl = await mp3.finish();
                if (fl && fl.tail && fl.tail.byteLength) await mp3Sink.write(new Uint8Array(fl.tail));
                mp3Blob = await mp3Sink.finish();
            }
            try { if (tmp.cleanup) await tmp.cleanup(); } catch (_) {}
            tapBefore.finish(); tapAfter.finish();
            var owned = [outSink, mp3Sink, beforeSink];
            return {
                wav: wavBlob, mp3: mp3Blob, mp3Kbps: mp3 ? opts.outputs.mp3Kbps : 0, before: beforeBlob,
                /** Removes this result's files from disk. Call when the result is replaced. */
                dispose: async function () {
                    for (var di = 0; di < owned.length; di++) { try { if (owned[di] && owned[di].cleanup) await owned[di].cleanup(); } catch (_) {} }
                    owned = [];
                },
                sampleRate: SR, samples: total, duration: total / SR,
                params: p, engine: engine, gainDb: 20 * Math.log10(gain), preGainDb: 20 * Math.log10(g0),
                wpe: wpeState, declip: declip ? clip : null,
                spectrogram: { before: tapBefore.cols, after: tapAfter.cols, bins: COL_FFT / 2, floor: DB_FLOOR, ceil: DB_CEIL },
                spectrum: { before: tapBefore.spectrum, after: tapAfter.spectrum },
                stats: { before: statBefore.summary(), after: statAfter.summary(), quiet: quietMoments(statBefore, statAfter) },
                bandwidth: { before: tapBefore.spectrum ? bandwidthFromDb(tapBefore.spectrum) : 0, after: tapAfter.spectrum ? bandwidthFromDb(tapAfter.spectrum) : 0 },
                timing: t
            };
        } catch (e) {
            // Close what this run opened: the model stream in the worker, the
            // MP3 encoder, and every file on disk.
            if (dfs) { try { await dfs.finish(dfnStrength); } catch (_) {} }
            if (mp3) { try { mp3.abort(); } catch (_) {} }
            var sinks = [tmp, outSink, mp3Sink, beforeSink];
            for (var si = 0; si < sinks.length; si++) { try { if (sinks[si] && sinks[si].cleanup) await sinks[si].cleanup(); } catch (_) {} }
            throw e;
        }
    }
    /* How much quieter the input's quiet moments became, relative to the
       speech, measured at the SAME 20-ms frames before and after (the output
       is sample-aligned with the input). «The quietest 10 % of the output»
       measured other moments: on read speech with few pauses they are quiet
       syllables, and the number did not move whatever the chain did. The
       quiet moments are the input's quietest 15 % that are not digital
       silence; a cleaned moment reads no lower than −80 dBFS. */
    function quietMoments(sb, sa) {
        var n = Math.min(sb.n, sa.n);
        if (!n) return null;
        var idx = [];
        for (var i = 0; i < n; i++) if (sb.vals[i] >= SILENT) idx.push(i);
        if (idx.length < 10) return null;
        idx.sort(function (a, b) { return sb.vals[a] - sb.vals[b]; });
        var k = Math.max(1, Math.floor(idx.length * 0.15)), eb = 0, ea = 0, F = Math.pow(10, -80 / 20);
        for (var j = 0; j < k; j++) { eb += sb.vals[idx[j]]; ea += Math.max(F, sa.vals[idx[j]]); }
        var b = sb.summary(), a = sa.summary();
        return {
            before: b.speechDb - 20 * Math.log10(eb / k),
            after: a.speechDb - 20 * Math.log10(ea / k)
        };
    }
    function bandwidthFromDb(d) {
        var peak = -Infinity, i;
        for (i = 0; i < d.length; i++) if (d[i] > peak) peak = d[i];
        var cut = d.length - 1;
        for (i = d.length - 1; i > 0; i--) { if (d[i] > peak - 40) { cut = i; break; } }
        return Math.round(cut * SR / (d.length * 2));
    }

    /* ── The room's tail, measured by the stage that removes it ─────────────
       How far below the speech the energy is that WPE takes away, dB, over
       the diagnosis windows, counting only speech-active 20-ms frames (within
       30 dB of the window's loudest). Measured 2026-09-26 on 165 files: dry
       speech (noise of any kind, phone, cassette) 20–32 dB, a 0.7 s room
       15–25, a 1.3 s hall 12–20, a classroom lecture 9–17; and WPE helps
       (ESTOI +0.03…0.06, OVRL up to +0.57) exactly where this is ≤ 18 dB, is
       worth nothing above ~19.5. So it is both the number the page shows and
       the switch for the stage — the old «reverb %» (one lag-0.1 s
       autocorrelation) read a 1.3 s hall 11 % and dishes in a kitchen 39 %. */
    var TAIL_ON_DB = 18;
    async function measureTail(source, diag, call, opts) {
        opts = opts || {};
        var wins = (diag && diag.windows) || [];
        var ex = 0, ed = 0, F = Math.floor(SR * 0.02);
        for (var i = 0; i < wins.length; i++) {
            if (opts.signal && opts.signal.aborted) throw abortErr();
            var x = await source.read(wins[i][0], wins[i][1]);
            if (x.length < SR) continue;
            var y = await call(x.slice(), {});
            if (!y || y.length !== x.length) throw new Error('wpe: length ' + (y && y.length) + ' for ' + x.length);
            var fr = [], mx = 0, f, k, s;
            for (f = 0; f + F <= x.length; f += F) { s = 0; for (k = 0; k < F; k++) s += x[f + k] * x[f + k]; fr.push(s); if (s > mx) mx = s; }
            for (var j = 0; j < fr.length; j++) {
                if (!(fr[j] > mx * 1e-3)) continue;
                ex += fr[j];
                for (k = 0; k < F; k++) { var d = x[j * F + k] - y[j * F + k]; ed += d * d; }
            }
            if (opts.onProgress) opts.onProgress((i + 1) / wins.length);
        }
        if (!(ex > 0)) return null;
        return Math.round(10 * Math.log10(ex / Math.max(ed, ex * 1e-10)) * 10) / 10;
    }

    var api = {
        SR: SR, BLOCK: BLOCK, CONTEXT: CONTEXT,
        Overlap: Overlap, XfadeOverlap: XfadeOverlap, WpeStage: WpeStage, SpeechBand: SpeechBand,
        FrameStats: FrameStats, Tap: Tap, fft: fft, spectrumDb: spectrumDb,
        params: params, depthClass: depthClass, PRESET_NAMES: PRESET_NAMES, DEEP_SNR_DB: DEEP_SNR_DB, DEEP_TAIL_DB: DEEP_TAIL_DB, NARROW_HZ: NARROW_HZ,
        preGain: preGain, loudnessGain: loudnessGain, makeFinalRender: makeFinalRender, makeFinalStream: makeFinalStream,
        openFfmpegSource: openFfmpegSource, openBufferSource: openBufferSource,
        analyze: analyze, run: run, quietMoments: quietMoments, measureTail: measureTail, TAIL_ON_DB: TAIL_ON_DB
    };
    root.SpeechRestorerEngine = api;
    if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
